import {
  getAppliedPubYearRange,
  getPubTypeValues,
  getPubYearDefaults,
  getSavedCheckboxStates,
  getSavedPubYearRange,
  setAppliedPubYearRange,
  setPubTypeValues,
  setSavedCheckboxStates,
  setSavedPubYearRange,
} from "../state/filters.js";
import { getAccessToken, getAuthHeaders, getSubscriptionStatusUrl, authenticatedFetch } from "../api/auth.js";
import { openSignInModal } from "./signin-modal.js";

export function initFilters() {
  const filterBtn = document.getElementById("filterBtn");
  const filterModal = document.getElementById("filterModal");
  const closeFilterModal = document.getElementById("closeFilterModal");
  const pubTypeCheckboxes = document.querySelectorAll(".pub-type-checkbox");
  const filterErrorMsg = document.getElementById("filterErrorMsg");
  const applyFilterBtn = document.getElementById("applyFilterBtn");
  const resetFilterBtn = document.getElementById("resetFilterBtn");

  // Back-compat globals used by pre bundle
  window.getPubYearRange = () => getAppliedPubYearRange();
  window.getPubTypeValues = () => getPubTypeValues();

  function updateFilterNotificationDot() {
    const notificationDot = document.querySelector(".filter-notification-dot");
    if (!notificationDot) return;

    const isPubTypeActive = getPubTypeValues().length > 0;
    const isYearActive = Boolean(getAppliedPubYearRange());

    if (isPubTypeActive || isYearActive) {
      notificationDot.classList.remove("hidden");
    } else {
      notificationDot.classList.add("hidden");
    }
  }

  function saveCheckboxStates() {
    const nextStates = {};
    pubTypeCheckboxes.forEach((checkbox) => {
      nextStates[checkbox.value] = checkbox.checked;
    });
    setSavedCheckboxStates(nextStates);
  }

  function restoreCheckboxStates() {
    const states = getSavedCheckboxStates();
    if (Object.keys(states).length === 0) return;

    pubTypeCheckboxes.forEach((checkbox) => {
      if (Object.prototype.hasOwnProperty.call(states, checkbox.value)) {
        checkbox.checked = states[checkbox.value];
      }
    });
  }

  function restorePubYearRange() {
    if (typeof window.setPublicationYearSliderValues === "function") {
      const r = getSavedPubYearRange();
      window.setPublicationYearSliderValues(r.start, r.end);
    }
  }

  function initializeSavedFilters() {
    saveCheckboxStates();
    restorePubYearRange();
    updateFilterNotificationDot();
  }

  let filterModalPreviousFocus = null;
  function getFocusableElements(container) {
    const selector = "button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])";
    return Array.from(container.querySelectorAll(selector)).filter(
      (el) => el.offsetParent !== null && !el.disabled
    );
  }
  function trapFocusFilter(e, dialogEl) {
    if (e.key !== "Tab") return;
    const focusable = getFocusableElements(dialogEl);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  function closeFilterModalWithRestore() {
    restoreCheckboxStates();
    restorePubYearRange();
    if (filterErrorMsg) filterErrorMsg.classList.add("hidden");
    const firstCheckbox = document.getElementById("filterPubTypeAll") || document.querySelector(".pub-type-checkbox[value=\"All\"]");
    if (firstCheckbox) firstCheckbox.setAttribute("aria-invalid", "false");

    // Remove focus trap and restore focus
    if (filterModal) {
      const dialogEl = filterModal.querySelector(".modal-content");
      if (dialogEl && dialogEl._filterModalTrapHandler) {
        document.removeEventListener("keydown", dialogEl._filterModalTrapHandler);
        dialogEl._filterModalTrapHandler = null;
      }
      filterModal.classList.add("hidden");
      if (filterModalPreviousFocus && typeof filterModalPreviousFocus.focus === "function") {
        filterModalPreviousFocus.focus();
        filterModalPreviousFocus = null;
      }
    }

  }

  // Initialize dot + saved UI state
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeSavedFilters);
  } else {
    initializeSavedFilters();
  }

  // Open filter modal
  if (filterBtn && filterModal) {
    filterBtn.addEventListener("click", async () => {
      restoreCheckboxStates();
      restorePubYearRange();
      if (filterErrorMsg) filterErrorMsg.classList.add("hidden");

      filterModalPreviousFocus = document.activeElement;
      filterModal.classList.remove("hidden");
      const dialogEl = filterModal.querySelector(".modal-content");
      if (dialogEl) {
        const focusable = getFocusableElements(dialogEl);
        if (focusable.length > 0) focusable[0].focus();
        const handler = (e) => trapFocusFilter(e, dialogEl);
        dialogEl._filterModalTrapHandler = handler;
        document.addEventListener("keydown", handler);
      }
    });
  }

  // Close filter modal via X
  if (closeFilterModal) {
    closeFilterModal.addEventListener("click", () => {
      closeFilterModalWithRestore();
    });
  }

  // Close modal on overlay click
  if (filterModal) {
    filterModal.addEventListener("click", (e) => {
      if (e.target === filterModal) {
        closeFilterModalWithRestore();
      }
    });
  }

  // Close modal on Escape
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && filterModal && !filterModal.classList.contains("hidden")) {
      closeFilterModalWithRestore();
    }
  });

  // Checkbox change logic (All vs individuals): when All is selected, unselect all other types
  pubTypeCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      const allCheckbox = document.querySelector('.pub-type-checkbox[value="All"]');

      if (checkbox.value === "All") {
        if (checkbox.checked) {
          pubTypeCheckboxes.forEach((cb) => {
            if (cb.value !== "All") cb.checked = false;
          });
        }
      } else {
        if (checkbox.checked && allCheckbox) {
          allCheckbox.checked = false;
        } else {
          const individualCheckboxes = Array.from(pubTypeCheckboxes).filter((cb) => cb.value !== "All");
          const allIndividualChecked = individualCheckboxes.every((cb) => cb.checked);
          if (allIndividualChecked && allCheckbox) allCheckbox.checked = true;
        }
      }

      const checkedCount = Array.from(pubTypeCheckboxes).filter((cb) => cb.checked).length;
      if (checkedCount > 0 && filterErrorMsg) {
        filterErrorMsg.classList.add("hidden");
        const firstCheckbox = document.getElementById("filterPubTypeAll") || document.querySelector(".pub-type-checkbox[value=\"All\"]");
        if (firstCheckbox) firstCheckbox.setAttribute("aria-invalid", "false");
      }
    });
  });

  // Apply button
  if (applyFilterBtn && filterModal) {
    applyFilterBtn.addEventListener("click", () => {
      const checkedCount = Array.from(pubTypeCheckboxes).filter((cb) => cb.checked).length;

      if (checkedCount === 0) {
        if (filterErrorMsg) {
          filterErrorMsg.textContent = "At least one publication type must be selected.";
          filterErrorMsg.classList.remove("hidden");
        }
        const firstCheckbox = document.getElementById("filterPubTypeAll") || document.querySelector(".pub-type-checkbox[value=\"All\"]");
        if (firstCheckbox) firstCheckbox.setAttribute("aria-invalid", "true");
        return;
      }

      const pubYearError = typeof window.getPublicationYearCustomValidationError === "function"
        ? window.getPublicationYearCustomValidationError()
        : null;
      if (pubYearError && filterErrorMsg) {
        filterErrorMsg.textContent = pubYearError;
        filterErrorMsg.classList.remove("hidden");
        const firstCheckbox = document.getElementById("filterPubTypeAll") || document.querySelector(".pub-type-checkbox[value=\"All\"]");
        if (firstCheckbox) firstCheckbox.setAttribute("aria-invalid", "true");
        return;
      }

      if (filterErrorMsg) filterErrorMsg.classList.add("hidden");
      const firstCheckbox = document.getElementById("filterPubTypeAll") || document.querySelector(".pub-type-checkbox[value=\"All\"]");
      if (firstCheckbox) firstCheckbox.setAttribute("aria-invalid", "false");

      const allCheckbox = document.querySelector('.pub-type-checkbox[value="All"]');
      if (allCheckbox && allCheckbox.checked) {
        setPubTypeValues([]);
      } else {
        const nextValues = Array.from(document.querySelectorAll(".pub-type-checkbox:checked"))
          .map((cb) => cb.value)
          .filter((val) => val !== "All");
        setPubTypeValues(nextValues);
      }

      saveCheckboxStates();

      if (typeof window.getPublicationYearSliderValues === "function") {
        const { start, end } = window.getPublicationYearSliderValues();
        const defaults = getPubYearDefaults();
        if (start !== defaults.start || end !== defaults.end) {
          setAppliedPubYearRange({ start, end });
          setSavedPubYearRange({ start, end });
        } else {
          setAppliedPubYearRange(null);
          setSavedPubYearRange({ ...defaults });
        }
      }

      updateFilterNotificationDot();
      filterModal.classList.add("hidden");
    });
  }

  // Reset (UI-only): only "All" selected, all other types unselected
  if (resetFilterBtn) {
    resetFilterBtn.addEventListener("click", () => {
      const allCheckbox = document.querySelector('.pub-type-checkbox[value="All"]');
      if (allCheckbox) allCheckbox.checked = true;

      pubTypeCheckboxes.forEach((cb) => {
        if (cb.value !== "All") cb.checked = false;
      });

      if (typeof window.setPublicationYearSliderValues === "function") {
        const defaults = getPubYearDefaults();
        window.setPublicationYearSliderValues(defaults.start, defaults.end);
      }

      if (filterErrorMsg) filterErrorMsg.classList.add("hidden");
      const firstCheckbox = document.getElementById("filterPubTypeAll") || document.querySelector(".pub-type-checkbox[value=\"All\"]");
      if (firstCheckbox) firstCheckbox.setAttribute("aria-invalid", "false");
      // Note: we intentionally do not apply state here; Apply handles it.
    });
  }

  // Ensure dot is initialized even if script loads after DOM
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", updateFilterNotificationDot);
  } else {
    updateFilterNotificationDot();
  }
}

